import { tool, type ToolSet } from "ai";
import { z } from "zod";

/**
 * Lume's tool surface — the contract between the agent and the UI.
 *
 * This module is deliberately free of React and of any model/provider import:
 * the future server route (POST /api/lume/chat) passes the same `lumeTools` to
 * streamText, and the client renders the resulting tool parts. One definition,
 * both sides.
 *
 * Nothing here has an `execute`: a widget tool's job is to be rendered, and
 * its output is resolved before it is streamed (or by the server route later).
 */

export const cadernoKindSchema = z.enum(["completo", "materias", "prova"]);

export const createCadernoWidget = tool({
  description:
    "Mostra ao aluno o caminho para criar um caderno de estudo, já com o ano " +
    "resolvido. Use quando o aluno quer começar a estudar.",
  inputSchema: z.object({
    /** Taxonomy year the caderno will be built for; null when still unknown */
    yearId: z.number().int().positive().nullable(),
    /**
     * DEAD, and kept only so the agent's own schema (a separate repo) can go on
     * sending it without failing validation. There used to be three ways to
     * build a caderno and this named the one to show first; there is one flow
     * now, so there is nothing left to single out and the widget ignores it.
     */
    highlight: cadernoKindSchema.nullable(),
  }),
  outputSchema: z.object({
    yearId: z.number().int().positive().nullable(),
    /** Resolved label ("9º ano"), so the widget doesn't have to look it up */
    yearLabel: z.string().nullable(),
    /** Dead — see the input above */
    highlight: cadernoKindSchema.nullable(),
  }),
});

/**
 * The screens the agent may link to. Mirrors lume/routes.ts in the agent service.
 *
 * IT HAS TO STAY A SUPERSET OF WHAT THE AGENT SENDS, and for a reason that is not
 * about rendering: stored messages are re-validated against these schemas on reload
 * (see chat-session.ts), so a route id missing from here would make an old
 * conversation containing that link fail validation and be dropped. Hence the ids
 * the service no longer emits are kept below rather than tidied away.
 */
export const lumeRouteSchema = z.enum([
  // Outside any caderno
  "inicio",
  "cadernos",
  // The build flow. It takes a yearId and carries it as `?ano=`, which is why the
  // built path can contain a query string.
  "novo",
  // Inside one
  "caderno_inicio",
  "trilha",
  // The trilha opened on one of the year's two trilhas (?modo=), which switches
  // the caderno to it — see router.tsx cadernoTrilhaRoute
  "trilha_completo",
  "trilha_essencial",
  "materias",
  "materia",
  "topico",
  "praticar",
  "exame",
  "pratica_hub",
  "simulado",
  // A simulado the student drew (create_simulado), keyed on its attempt
  "simulado_livre",
  // The Simulados screen and one of its provas, at both levels
  "simulados",
  "prova",
  // The three ids the flow used to have, still accepted: they are gone from the
  // service's route table, but they are still sitting in threads that were had
  // while it had them. All three land on /novo (router.tsx).
  "novo_completo",
  "novo_materias",
  "novo_prova",
  // The corrector. Four ids for three routes: the chooser and the writer share
  // one path and are told apart by `?tema=`, and "omit an id and you land
  // somewhere else" is a rule a model gets wrong. Two named destinations is not.
  "redacao_temas",
  "redacao_escrever",
  "redacao_minhas",
  "redacao_correcao",
]);

/**
 * The navigation button the agent draws in the chat.
 *
 * Declared here for two reasons beyond rendering. First, `safeValidateUIMessages`
 * checks stored messages against these schemas on reload
 * (agent/chat-session.ts) — a conversation containing a link would be thrown away
 * as unrecognised if the tool were missing. Second, the shapes are the contract
 * with the agent's own `show_link` tool, so a change on either side should be a
 * visible mismatch here rather than a widget that silently renders nothing.
 *
 * `path` is built by the agent from a route id and ids, never written by the model
 * — the widget navigates to it as given.
 */
export const showLink = tool({
  description:
    "Mostra ao aluno um botão que abre uma página do app. O caminho é montado " +
    "a partir da rota e dos ids, nunca escrito à mão.",
  inputSchema: z.object({
    route: lumeRouteSchema,
    label: z.string(),
    reason: z.string().nullish(),
    cadernoId: z.number().int().positive().nullish(),
    subjectId: z.number().int().positive().nullish(),
    termId: z.number().int().positive().nullish(),
    /** One simulado, for the simulado and prova routes */
    simuladoId: z.number().int().positive().nullish(),
    /** A drawn simulado's attempt, for the simulado_livre route */
    attemptId: z.number().int().positive().nullish(),
    yearId: z.number().int().positive().nullish(),
    /** essays_themes.id — the redação proposta, for redacao_escrever */
    themeId: z.number().int().positive().nullish(),
    /** essays_corrections.id — one correction, for redacao_correcao */
    correctionId: z.number().int().positive().nullish(),
  }),
  outputSchema: z.object({
    widget: z.literal("link"),
    route: z.string(),
    /** Absolute app path, basepath included: /lume/caderno/3/... */
    path: z.string(),
    label: z.string(),
    reason: z.string().nullish(),
  }),
});

/**
 * A caderno the agent filled in, for the student to confirm.
 *
 * The shapes mirror `suggest_caderno` in the agent service (lume/suggest-caderno.ts),
 * which has already checked them against the rules `CadernoService::create` enforces —
 * so a proposal that reaches here is one the create endpoint should accept.
 *
 * The widget still posts it through the ordinary endpoint and still shows a 422
 * verbatim: the agent cannot know whether a temaId belongs to that matéria, only the
 * database does. And note what the payload does NOT carry — names. The card resolves
 * every label from the taxonomy itself, because a model-written matéria name that
 * disagrees with the tema it sits above is worse than a slower card.
 */
export const suggestCaderno = tool({
  description:
    "Mostra ao aluno um caderno já preenchido — tipo, ano, matérias, temas e " +
    "data da prova — que ele cria com um clique. Não cria nada por si.",
  inputSchema: z.object({
    kind: cadernoKindSchema,
    yearId: z.number().int().positive(),
    title: z.string().nullish(),
    subjects: z
      .array(
        z.object({
          subjectId: z.number().int().positive(),
          scope: z.enum(["full", "partial"]),
          temaIds: z.array(z.number().int().positive()),
        })
      )
      .nullish(),
    examDate: z.string().nullable().optional(),
    reason: z.string().nullish(),
  }),
  outputSchema: z.object({
    widget: z.literal("caderno_proposal"),
    kind: cadernoKindSchema,
    yearId: z.number().int().positive(),
    title: z.string().nullish(),
    subjects: z.array(
      z.object({
        subjectId: z.number().int().positive(),
        scope: z.enum(["full", "partial"]),
        temaIds: z.array(z.number().int().positive()),
      })
    ),
    examDate: z.string().nullable(),
    reason: z.string().nullish(),
  }),
});

/**
 * A question from the tópico's own bank, asked inside the thread.
 *
 * THE AGENT NAMES A TÓPICO AND NOTHING ELSE — no stem, no options, no answer. The
 * widget draws the question from the app and scores it (agent/practice-run.ts), so
 * the model never holds a gabarito it could give away. That is the whole design,
 * and it is why this tool's input looks so thin: everything it does NOT carry is
 * deliberate.
 *
 * There is no output beyond the instruction to render. What came of it reaches the
 * agent the way every other screen fact does — on the next turn's page context, as
 * `practiceAnswer` (agent/request-context.ts).
 */
export const askPracticeQuestion = tool({
  description:
    "Mostra ao aluno uma questão real do tópico, dentro da conversa. Você não " +
    "escolhe nem vê a questão: indica o tópico e a app trata do resto.",
  inputSchema: z.object({
    /** Optional: without one the question is shown but scores nothing */
    cadernoId: z.number().int().positive().nullish(),
    /** The TÓPICO being checked — never a tema, which has no question bank of its own */
    topicoId: z.number().int().positive(),
    reason: z.string().nullish(),
  }),
  outputSchema: z.object({
    widget: z.literal("practice_question"),
    cadernoId: z.number().int().positive().nullish(),
    topicoId: z.number().int().positive(),
    reason: z.string().nullish(),
  }),
});

/**
 * Two to four tappable answers, under a reply that asked something.
 *
 * The cheapest widget in the set and the one that removes the most friction. Every
 * question Lume asks currently costs the student a sentence typed on a phone — "de
 * qual caderno?", "prefere que eu explique ou quer praticar?" — and the answer is
 * almost always one of two or three things the agent could have named. A chip is
 * that answer, one tap.
 *
 * WHAT IT IS NOT is a menu of what Lume can do. The failure mode here is a chat that
 * turns into an IVR: every turn ending in four buttons, the student never typing, and
 * the one thing this product has over a form — that you can ask it anything — quietly
 * disappearing. Hence the prompt rule: only when the agent genuinely needs a choice
 * made, never as decoration.
 *
 * Each option is sent VERBATIM as the student's own next message, so it is written in
 * their voice ("Explica de novo") and not in the agent's ("Explicar de novo").
 */
export const quickReplies = tool({
  description:
    "Oferece ao aluno 2 a 4 respostas prontas para tocar. O texto escolhido " +
    "é enviado como a próxima mensagem dele.",
  inputSchema: z.object({
    options: z.array(z.string()).min(2).max(4),
  }),
  outputSchema: z.object({
    widget: z.literal("quick_replies"),
    options: z.array(z.string()),
  }),
});

/**
 * How the student is doing, as a card instead of a paragraph.
 *
 * VIEW ONLY, and the only widget here that is. It carries no numbers: the agent
 * names a caderno and the card reads the same queries the caderno's own screens
 * read. That is the point of it — the values are the ones the prompt works hardest
 * to stop the agent quoting (mastery is 0–1 and unspeakable, percentage and mastery
 * are two different numbers that both sound like "how far along"), and a widget that
 * cannot be misquoted retires a whole class of rule.
 */
export const showProgress = tool({
  description:
    "Mostra ao aluno um cartão com o progresso do caderno: percentagem, " +
    "trilha, domínio por matéria. Você não escreve os números.",
  inputSchema: z.object({
    cadernoId: z.number().int().positive(),
  }),
  outputSchema: z.object({
    widget: z.literal("progress"),
    cadernoId: z.number().int().positive(),
  }),
});

/**
 * The offer of a tópico's Toda Matéria manual to a student whose plan does not
 * open it. The agent names the tópico; the widget reads the preview
 * (/api/lume/manual-preview/) and draws nothing for a premium student or an
 * unlocked manual.
 */
export const showUnlock = tool({
  description:
    "Mostra ao aluno sem TM+ o que está bloqueado — o material de um tópico, ou " +
    "o simulado da semana já usado — com o caminho para liberar.",
  inputSchema: z.object({
    motivo: z.enum(["material", "simulado"]).nullish(),
    termId: z.number().int().positive().nullish(),
    cadernoId: z.number().int().positive().nullish(),
  }),
  outputSchema: z.object({
    widget: z.literal("unlock"),
    motivo: z.enum(["material", "simulado"]).nullish(),
    termId: z.number().int().positive().nullish(),
    cadernoId: z.number().int().positive().nullish(),
  }),
});

export const lumeTools = {
  // Snake case: the name reaches the model, and it is the part type the UI
  // switches on (`tool-create_caderno_widget`)
  create_caderno_widget: createCadernoWidget,
  suggest_caderno: suggestCaderno,
  show_link: showLink,
  ask_practice_question: askPracticeQuestion,
  quick_replies: quickReplies,
  show_progress: showProgress,
  show_unlock: showUnlock,
} satisfies ToolSet;

/**
 * Tool parts that DO something when clicked, so only the newest of each may be live.
 *
 * A thread is a scrollback: every card ever rendered is still on screen and still
 * looks pressable. For a link that is fine — an older "ler o material de Funções"
 * still goes somewhere sensible, which is why `show_link` is deliberately NOT here;
 * disabling it would edit the conversation as the student scrolls back through it.
 *
 * For a card that ACTS, staleness is a trap. Two caderno proposals in one thread are
 * not a choice: the student clicks the one in front of them rather than the current
 * one, and creates the caderno the conversation abandoned two messages ago. A
 * question from earlier is the same shape of problem — answering it scores into a
 * run the conversation has moved past, and it reads as the answer to what was just
 * asked.
 *
 * SUPERSEDING IS PER TYPE, not across all of them (see superseded.ts). A caderno
 * proposal and a practice question are different actions with different lifetimes,
 * and letting one silence the other would retire a live proposal because Lume
 * happened to ask a question after it.
 */
export const SUPERSEDING_PART_TYPES: readonly string[] = [
  "tool-create_caderno_widget",
  "tool-suggest_caderno",
  "tool-ask_practice_question",
];

/**
 * `quick_replies` is the one actionable widget deliberately NOT in the list above,
 * and the reason is that it has a strictly better rule available.
 *
 * Chips are live only while their message is the LAST one in the thread (ChatBubble).
 * That subsumes superseding — an older set is by definition not in the last message —
 * and it also covers the case the toolCallId mechanism cannot: a set the student
 * already used. There is no newer card to supersede it, only a newer MESSAGE, and
 * after a reload a module-scope "already used" flag would be gone while the message
 * list is still right there.
 *
 * So they retire on the conversation moving on, which is the same moment the offer
 * stopped being the current one.
 */

export type LumeRouteName = z.infer<typeof lumeRouteSchema>;

export type CadernoKindHint = z.infer<typeof cadernoKindSchema>;
